/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl, getImageUrl } from "@Utils";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import { useTranslation } from "@i18n";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import * as Sentry from "@Sentry";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import SharingContent from "./SharingContent/SharingContent";
import VersionContent from "./VersionContent/VersionContent";
import RightSidebarFileExp from "@Components/RightSidebarFileExp/RightSidebarFileExp";
import DetailsContent from "./DetailsContent/DetailsContent";
import DependencyContent from "./DependencyContent/DependencyContent";
import ConstituentContent from "./ConstituentContent/ConstituentContent";
import ExternalLinksContent from "./ExternalLinksContent/ExternalLinksContent";
import { isExternalSharingEnabled } from "../ExternalLinks";
import AccessContent from "./AccessContent/AccessContent";
import { showToastNotification } from "@Reducers/toastSlice";
import { BIM_ENGINE, CAD_ENGINE, SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import RevisionContent from "./RevisionContent/RevisionContent";
import FolderRevisionSeriesContent from "./FolderRevisionSeriesContent/FolderRevisionSeriesContent";
import FileRevisionSeriesContent from "./FileRevisionSeriesContent/FileRevisionSeriesContent";
import IconComponent from "@CubeComponents/icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AccordionSummary, AccordionDetails } from "@mui/material";
import AllFileVersionStatusInReviewsModal from "@Features/reviews/AllFileVersionStatusInReviewsModal";
import FileStatusInReviews from "@Features/reviews/FileStatusInReviews";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { AiOutlinePlusCircle } from "react-icons/ai";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { RxCross2 } from "react-icons/rx";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import MultiSelectComponent from "@CubeComponents/form/MultiselectComponent";
import axiosWrapper from "@AxiosWrapper";
import { useParams } from "react-router-dom";
import {
  maximiseFileUpload,
  setIsFileVersionUploading,
  setFileUploadProperties,
  setCurrentFileUploadProperties,
  selectedFilesFileManager,
} from "@Actions/fileUpload";
import CustomFieldsContent from "./CustomFieldsContent";
import _ from "lodash";
import type { DirectoryDataFileItem, GetFileDetailsResponse } from "@ServerTypes/fileOperations/fileOperations.schemas";
import type {
  GetFileNomenclatureResponse,
  SetFileNomenclatureResponse,
} from "@ServerTypes/fileUpload/fileUpload.schema";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { ProjectFormTemplate } from "@Features/projects";
import Text from "@CubeComponents/html/Text/Text";
import { getIconbasedOnResourceType } from "@CubeComponents/constants/globalFunctions";
import { convertDate } from "@Services/luxonHelper";
import { buildDetailsContentProps, buildSharingContentProps } from "./fileVersionContentProps";
import { fetchDirectoryDataOfFile } from "@Actions/fileDirectoryData";
/**
 * One field of the folder nomenclature rule. Taken from the endpoint's own response schema rather
 * than re-declared, so the editor cannot drift from what the API stores — in particular
 * `allowedCharacters` is a NUMBER there, which the hand-written copy used to call a string.
 */
type NomenclatureField = NonNullable<NonNullable<GetFileNomenclatureResponse["data"]>["fields"]>[number];

/**
 * The file row this panel renders: exactly what `getDirectoryDataOfFile` returns —
 * `SelectedFileItem` plus the members the file-manager attaches and this panel reads (creator/date
 * display fields, the transmittal back-reference, `path`, the owning project).
 */
type FileVersionRow = DirectoryDataFileItem;

/**
 * The nomenclature rule as this panel HOLDS it. Both members are total here even though the API
 * leaves them optional: `getFileNomenclature` normalises an absent separator/field list to
 * `""` / `[]` on the way in, so nothing downstream has to re-check them.
 */
type NomenclatureData = { separator: string; fields: NomenclatureField[] };

const DEFAULT_EMPTY_ARRAY = [] as FileVersionRow[];

/**
 * Scoped error boundary for one details-panel section. The panel builds every allowed section
 * inline with no boundary above it, so before this a single throwing section rendered the whole
 * panel dead — which a tester can only describe as "the details panel is not opening". Sentry gets
 * the error as a handled, section-tagged event instead of a page crash.
 */
class PanelSectionBoundary extends React.Component<
  { sectionId: string; fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    Sentry.captureException(error, {
      tags: { component: "FileVersionPanelSection" },
      extra: { section: this.props.sectionId, info },
    });
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

function FileVersion({
  disableEngineType,
  onChange,
  fileVersionId = [],
  selectedFileDataFromFileManager = DEFAULT_EMPTY_ARRAY,
  isContainer = false,
  transmittalId,
}: {
  disableEngineType?: boolean;
  onChange?: (data: Record<string, unknown>) => void;
  fileVersionId?: string[];
  selectedFileDataFromFileManager?: FileVersionRow[];
  isContainer?: boolean;
  transmittalId?: ObjectIdString;
}) {
  const { t } = useTranslation();
  const params = useParams();
  const layout = useSelector((state: RootState) => state.layout);
  const fileUploadState = useSelector((state: RootState) => state.fileUploadState);
  const projectData = useSelector((state: RootState) => state.projectData);
  const fileUploadList = useSelector((state: RootState) => state.fileUploadList);
  const currentFolder = useSelector((state: RootState) => state.currentFolder);
  const dispatch = useDispatch();
  const specialCharRegex = /[\\/:*?"<>|]/;
  const versionManagementRef = useRef<HTMLDivElement | null>(null);
  const accordianInitialState = {
    openDetails: true,
    openSign: true,
    openNomenclature: true,
    openAccess: true,
    openConstituent: true,
    openSharing: true,
    openVersion: true,
    openDependency: true,
    openRevision: true,
    openStatusInReviews: true,
    openCustomFiles: true,
    openCustomMetadata: true,
    // Expanded by default (QA item 12) — consistency with the neighbouring sections won over the
    // request-per-selection cost this used to avoid.
    openExternalLinks: true,
  };

  const [openAllReviews, setOpenAllReviews] = useState(false);
  const [nomenclatureData, setNomenclatureData] = useState<NomenclatureData>({
    separator: "",
    fields: [],
  });
  const [accordianState, setAccordianState] = useState<Record<string, boolean>>(accordianInitialState);
  const [selectedFormTemplateData, setSelectedFormTemplateData] = useState<{
    formTemplate?: string;
    formTemplateId?: string;
  }>({});
  const [updatedFormTemplateId, setUpdatedFormTemplateId] = useState<string | null>(null);
  const [separatorError, setSeparatorError] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<FileVersionRow[]>([]);
  const [nomenclatureFilled, setNomenclatureFilled] = useState<boolean>(false);
  const [multipleFilesSelected, setMultipleFilesSelected] = useState<boolean>(false);

  const getFileDirectoryData = async () => {
    const data = await fetchDirectoryDataOfFile(
      [{ field: isContainer ? "_id" : "fileVersionId", value: fileVersionId[0] }],
      transmittalId,
    );
    if (Array.isArray(data)) {
      setSelectedFile(data);
      dispatch(selectedFilesFileManager(data));
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  useEffect(
    () => () => {
      if (nomenclatureFilled && !nomenclatureData.separator) {
        handleNomenclatureUpdate({
          separator: "",
          fields: [],
        });
      }
    },

    [],
  );

  const selectedFileFromManagerId = selectedFileDataFromFileManager[0]?._id;
  const selectedFileFromManagerTransmittalId = selectedFileDataFromFileManager[0]?.id;
  useEffect(() => {
    setMultipleFilesSelected(false);
    setSelectedFile([]);
    if (selectedFileDataFromFileManager.length == 1) {
      setSelectedFile(selectedFileDataFromFileManager);
    } else if (selectedFileDataFromFileManager.length > 1) {
      setMultipleFilesSelected(true);
    } else if (fileVersionId.length === 1) {
      getFileDirectoryData();
    } else if (fileVersionId.length > 1) {
      setMultipleFilesSelected(true);
    }
  }, [
    fileVersionId.length,
    selectedFileDataFromFileManager,
    selectedFileDataFromFileManager.length,
    selectedFileFromManagerId,
    selectedFileFromManagerTransmittalId, // patch for transmittal
  ]);

  let originalResourceType: string | undefined;

  const getFileNomenclature = async () => {
    const response = await axiosWrapper.post<GetFileNomenclatureResponse>(
      `${getBackendUrl()}/fileUpload/getFileNomenclature`,
      {
        folderId: selectedFile[0]!.hierarchyId,
      },
    );
    const fileNomenclature = response.data.data;
    if (fileNomenclature) {
      setNomenclatureData({ separator: fileNomenclature.separator ?? "", fields: fileNomenclature.fields ?? [] });
    } else {
      setNomenclatureData({
        separator: "",
        fields: [],
      });
    }
  };

  useEffect(() => {
    if (_.get(selectedFile, "[0].hierarchyId")) {
      let { resourceType } = selectedFile[0]!;

      if (resourceType == "RESOURCE_TYPE_SHORTCUT_FILE") {
        originalResourceType = "RESOURCE_TYPE_SHORTCUT_FILE";
        resourceType = "RESOURCE_TYPE_FILE";
      } else if (resourceType == "RESOURCE_TYPE_SHORTCUT_MERGED_FILE") {
        originalResourceType = "RESOURCE_TYPE_SHORTCUT_MERGED_FILE";
        resourceType = "RESOURCE_TYPE_MERGED_FILE";
      }
      if (resourceType == "RESOURCE_TYPE_FOLDER") {
        getFileNomenclature();
      }
    }
  }, [selectedFile]);

  const selectedRow = selectedFile[0];
  const getFormTemplate = useCallback(async () => {
    if (!selectedRow || selectedRow.resourceType === "RESOURCE_TYPE_TRANSMITTAL") return;
    const payload = {
      id: selectedRow.hierarchyId,
    };
    // `getFileDetails` answers with the document itself, and its own request schema documents that
    // answer as "the document directly (not a StandardResponse wrapper), or null if no such resource
    // exists" (fileOperations.schemas.ts, getFileDetailsRequest.meta) — an empty body reaches here as
    // a falsy `data`. `AxiosResponse<T>.data` claims otherwise, so the response type is declared
    // nullable and the guard below is a real check, not a formality.
    const response = await axiosWrapper.post<GetFileDetailsResponse | null>(
      `${getBackendUrl()}/fileOperations/getFileDetails`,
      payload,
    );
    const fileDetails = response.data;
    if (!fileDetails) return;
    setSelectedFormTemplateData({
      formTemplate: fileDetails.formTemplate,
      formTemplateId: fileDetails.formTemplateId,
    });
  }, [selectedRow]);

  useEffect(() => {
    if (_.get(selectedFile, "[0].hierarchyId")) {
      getFormTemplate();
    }
  }, [selectedFile, updatedFormTemplateId, getFormTemplate]);

  const accessContent = () => (
    <AccessContent
      resourceId={selectedFile[0]!.hierarchyId}
      resourceType={selectedFile[0]!.resourceType!}
      parentId={selectedFile[0]!.parentId}
      transmittalId={transmittalId ?? selectedFile[0]!.transmittalVersionId}
      transmittalHasFileId={selectedFile[0]!.transmittalHasFileId}
      spaceId={selectedFile[0]!.spaceId!}
      pathofFolder={selectedFile[0]!.path}
    />
  );

  const handleNomenclatureUpdate = (data: NomenclatureData) => {
    setNomenclatureData(data);
    if (
      (data.separator && data.fields.length && !specialCharRegex.test(data.separator)) ||
      (!data.separator && !data.fields.length)
    ) {
      setSeparatorError("");
      saveFileNomenclature(data);
    }
  };

  const handleSeparatorChange = (args: React.ChangeEvent<HTMLInputElement>) => {
    setNomenclatureFilled(true);
    const separatorValue = args.target.value;

    if (!separatorValue && nomenclatureData.fields.length) {
      setSeparatorError("Separator cannot be empty");
    } else if (specialCharRegex.test(separatorValue)) {
      setSeparatorError('Separator cannot contain any of the following characters: \\ / : * ? " < > |');
    } else {
      setSeparatorError("");
    }
    handleNomenclatureUpdate({
      ...nomenclatureData,
      separator: separatorValue,
    });
  };
  // The row index used to be read back out of the clicked node's `data-index` attribute; it is
  // already in scope where the row is rendered, so it is passed directly.
  const handleRemoveField = (index: number) => {
    const tempNomenclatureData = { ...nomenclatureData };
    tempNomenclatureData.fields.splice(index, 1);
    handleNomenclatureUpdate(tempNomenclatureData);
  };

  const handleCreateNewField = () => {
    handleNomenclatureUpdate({
      ...nomenclatureData,
      fields: [
        ...nomenclatureData.fields,
        {
          type: "text",
        },
      ],
    });
  };

  const nomenclatureFields = [
    {
      text: "Text",
      value: "text",
    },
    {
      text: "Single Select",
      value: "single select",
    },
    {
      text: "Number",
      value: "number",
    },
  ];

  const handleFieldTypeChange = (value: string | undefined, index: number) => {
    const tempNomenclatureData: NomenclatureData = { ...nomenclatureData };
    tempNomenclatureData.fields[index]!.type = value;
    handleNomenclatureUpdate(tempNomenclatureData);
  };

  const saveFileNomenclature = async (nomenclatureData: NomenclatureData) => {
    if (!selectedFile.length) return;
    if (!selectedFile[0]?.permission?.write) {
      showToast();
      return;
    }
    const data = {
      folderId: selectedFile[0]!.hierarchyId,
      fileNomenclature: nomenclatureData,
    };
    await axiosWrapper.post<SetFileNomenclatureResponse>(`${getBackendUrl()}/fileUpload/setFileNomenclature`, data);
  };

  const openAllReviewsCallback = () => {
    setOpenAllReviews(true);
  };

  const handleFieldValueChange = (value: number | null, index: number) => {
    const tempNomenclatureData: NomenclatureData = { ...nomenclatureData };
    // The numeric input models "empty" as `null`; `setFileNomenclature` models it as absent
    // (`allowedCharacters: z.number().optional()`), and rejects an explicit null.
    tempNomenclatureData.fields[index]!.allowedCharacters = value ?? undefined;
    handleNomenclatureUpdate(tempNomenclatureData);
  };

  const handleSingleSelectOptionsChange = (options: string[] | undefined, index: number) => {
    const tempNomenclatureData: NomenclatureData = { ...nomenclatureData };
    tempNomenclatureData.fields[index]!.options = options;
    handleNomenclatureUpdate(tempNomenclatureData);
  };
  const nomenclatureContent = () => (
    <div className="text-sm flex flex-col gap-4">
      <div className="flex gap-4 items-center">
        <span>
          <Text>{t("Separator")}</Text>
        </span>
        <div className="flex flex-col gap-1">
          <TextFieldComponent
            placeholder={t("Enter Separator")}
            value={nomenclatureData.separator}
            onChange={handleSeparatorChange}
            width="7.5rem"
          />
          {separatorError ? (
            <span className="text-red-600 text-sm">
              <Text>{separatorError}</Text>
            </span>
          ) : null}
        </div>
      </div>

      {nomenclatureData.fields.map((data: NomenclatureField, index: number) => (
        <div key={`nomenclatureRules_${index}`} className="flex flex-wrap gap-4 items-center">
          <span className="shrink-0">
            <Text>{`Field ${index + 1}`}</Text>
          </span>
          <div className="flex flex-wrap gap-4 items-center">
            <AutoComplete
              options={nomenclatureFields}
              valueField="value"
              getOptionLabel={(option) => option.text || ""}
              value={data.type}
              onChange={(_event: unknown, newValue: { text?: string; value?: string } | null) => {
                handleFieldTypeChange(newValue?.value, index);
              }}
              sx={{ width: "7.5rem", minWidth: "7.5rem" }}
              renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select Type")} />}
            />
            {(["text", "number"] as (string | undefined)[]).includes(data.type) && (
              <NumericTextBoxComponent
                placeholder={t("Allowed no. of characters")}
                value={data.allowedCharacters}
                change={(e) => handleFieldValueChange(e.value, index)}
                width="12rem"
                min={0}
              />
            )}
            {data.type == "single select" && (
              <MultiSelectComponent
                value={data.options}
                allowCustomValue
                // `MultiSelectComponentProps.change` is method shorthand precisely so a call site can
                // annotate the payload it knows it gets: this picker has no datasource, so the
                // component runs in string mode and hands back the typed values.
                change={(args: { value?: string[] }) => handleSingleSelectOptionsChange(args.value, index)}
                data-index={index}
                width="8.75rem"
              />
            )}
            <RxCross2 data-index={index} onClick={() => handleRemoveField(index)} className="cursor-pointer shrink-0" />
          </div>
        </div>
      ))}
      <AiOutlinePlusCircle onClick={handleCreateNewField} size={18} className="cursor-pointer" />
    </div>
  );

  const customFieldsContent = () => {
    const { hierarchyId, resourceType, permission } = selectedFile[0]!;
    // Every row the file manager can select carries both ids; the response schema still types them
    // optional, so the section is withheld rather than mounted against an undefined resource.
    if (!hierarchyId || !resourceType) return null;
    return (
      <CustomFieldsContent
        hierarchyId={hierarchyId}
        setUpdatedFormTemplateId={setUpdatedFormTemplateId}
        selectedFormTemplateData={selectedFormTemplateData}
        resourceType={resourceType}
        accessPermission={permission ?? {}}
        onChange={onChange}
      />
    );
  };

  /**
   * The row as `ProjectFormTemplate` takes it: forwarded whole, with only `permission` translated —
   * the row schema makes it nullable, the template panel models "no permission block" as absent.
   *
   * MEMOISED ON PURPOSE, and this is a runtime change from the inline object it replaces.
   * `FormTemplate` keys its init effect off this prop BY IDENTITY
   * (`useEffect(..., [resourceType, projectId, selectedFile])`, FormTemplate.tsx:126-135). That
   * effect POSTs `projects/getFormTemplateId` when the row carries no `modelFormTemplateId`
   * (FormTemplate.tsx:106) and re-seeds `selectedFormTemplateInfo` off the row when it does — so as
   * an inline object, freshly built each time, one of those two ran on EVERY render of this
   * sidebar, and toggling any accordion re-renders the whole sidebar
   * (`handleSingleAccordionStateUpdate` -> `setAccordianState`, wired to every `onChange` below).
   * The identity now changes when the `selectedFile` state array is replaced.
   *
   * NOT a cache: the effect that owns `selectedFile` re-runs on `selectedFileDataFromFileManager`'s
   * identity, so a caller that rebuilds that array every render still replaces the row and still
   * re-runs the child's effect. This is a ceiling on the re-runs, not a promise of one.
   *
   * (An earlier version of this note cited "once per keystroke in the nomenclature editor". That is
   * wrong: the nomenclature editor is gated on `RESOURCE_TYPE_FOLDER` and this section on
   * `engineType` being BIM or CAD, so the two are never on screen together.)
   */
  const formTemplateRow = useMemo(
    () => (selectedRow ? { ...selectedRow, permission: selectedRow.permission ?? undefined } : undefined),
    [selectedRow],
  );

  const customMetadataContent = () => (
    <ProjectFormTemplate
      selectedFile={formTemplateRow}
      projectId={projectData._id!}
      resourceType={selectedFile[0]!.engineType == "BIM" ? "model" : "drawing"}
      engineType={selectedFile[0]!.engineType}
    />
  );

  const sharingContent = () =>
    selectedFile[0]!.permission && !selectedFile[0]!.permission.share ? (
      <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={showToast} data-testid="shareContentBtn">
        <Text>{t("Transmit")}</Text>
      </ButtonComponent>
    ) : (
      <SharingContent
        {...buildSharingContentProps(selectedFile[0]!, {
          transmittalId,
        })}
      />
    );

  const revisionContent = () => {
    const { resourceType, hierarchyId } = selectedFile[0]!;
    // Both revision panels key their fetches off the resource id; without one there is nothing to
    // look up, so the section stays empty rather than querying `undefined`.
    if (!hierarchyId) return null;
    if (resourceType === "RESOURCE_TYPE_FOLDER") {
      return <FolderRevisionSeriesContent folderId={hierarchyId} />;
    }
    return (
      <>
        <FileRevisionSeriesContent fileId={hierarchyId} />
        <RevisionContent
          {...{
            resourceType: resourceType,
            resourceId: hierarchyId,
          }}
        />
      </>
    );
  };

  const fileStatusInReviewsContent = () => {
    const { fileVersion, spaceId } = selectedFile[0]!;
    // `folderItemSchema.fileVersion` is a plain string, but the reviews lookup takes a branded
    // ObjectId — parse rather than assert, so a row without a version id renders nothing instead of
    // sending an unusable id to `getReviewsListForEntity`.
    const fileVersionId = objectIdString.safeParse(fileVersion).data;
    if (!fileVersionId || !spaceId) return null;
    return (
      <FileStatusInReviews
        fileVersionId={fileVersionId}
        openAllReviewsCallback={openAllReviewsCallback}
        spaceId={spaceId}
      />
    );
  };

  const showToast = () => {
    dispatch(
      showToastNotification({
        content: t("Insufficient permission to perform this action."),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const dependencyContent = () => {
    const { fileVersion, isEmptyContainer, permission, project } = selectedFile[0]!;
    const projectId = project?._id || projectData.id;
    // Both ids are the keys the dependency list is fetched and cached under; neither has a sensible
    // empty value, so the section is withheld when the row carries neither.
    if (!fileVersion || !projectId) return null;
    return (
      <DependencyContent
        workspaceId={params.workspaceId}
        fileVersionId={fileVersion}
        isEmptyContainer={isEmptyContainer}
        accessPermission={permission ?? undefined}
        projectId={projectId}
      />
    );
  };

  const versionContent = () => {
    // Falls back to the route's Space when the row omits its own, matching every other panel here.
    const spaceId = selectedFile[0]!.spaceId || params.workspaceId;
    if (!spaceId) return null;
    return (
      <VersionContent
        filesData={selectedFile[0]}
        enableFileManagerContent={true}
        enableCostdatabaseContent={false}
        enableClashContent={false}
        clashVersionsDetails={[]}
        costDatabaseDetails={null}
        handleFileUploadButtonClick={handleFileUploadButtonClick}
        originalResourceType={originalResourceType!}
        spaceId={spaceId}
      />
    );
  };

  const getUserAvatar = (id: string) => <UserNameTagComponent id={id} />;

  const detailsContent = () => (
    <DetailsContent
      {...buildDetailsContentProps(selectedFile[0]!, {
        disableEngineType,
        transmittalId,
        getUserAvatar,
        projectId: projectData.id,
      })}
    />
  );

  const externalLinksContent = () => (
    <ExternalLinksContent
      resourceId={selectedFile[0]!.hierarchyId}
      spaceId={selectedFile[0]!.spaceId}
      name={selectedFile[0]!.name}
      resourceType={selectedFile[0]!.resourceType}
      // Same proxy the context menu uses. The row carries no flag for full access
      // (RESOURCE_CHANGE_PERMISSION is never mapped by `resolvePermissions`), and no full-access user
      // lacks `share`, so this withholds Manage from users who certainly cannot use it while the
      // server stays the authority.
      canManage={!!selectedFile[0]!.permission?.share}
    />
  );
  const constituentContent = () => (
    <ConstituentContent
      {...({
        fileVersionId: selectedFile[0]!.fileVersion,
        hierarchyId: selectedFile[0]!.hierarchyId,
        workspaceId: selectedFile[0]!.spaceId || params.workspaceId,
        accessPermission: selectedFile[0]!.permission,
        MergeName: selectedFile[0]!.name,
        resourceType: selectedFile[0]!.resourceType,
        projectId: selectedFile[0]!.project?._id || projectData.id,
      } as Record<string, unknown>)}
    />
  );
  const handleFileUploadButtonClick = () => {
    if (layout.FILE_MANAGER?.IMPORT_ONLY_UPLOADS) {
      return;
    }
    if (!selectedFile[0]!.permission?.upload) {
      showToast();
      return;
    }
    if (fileUploadState != "CLOSED") {
      dispatch(
        showToastNotification({
          content: t("Please close other upload operations before proceeding!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      dispatch(
        setFileUploadProperties({
          [`${selectedFile[0]!.spaceId}_${selectedFile[0]!.hierarchyId}`]: {
            workspaceId: selectedFile[0]!.spaceId,
            fileUploadList,
          },
        }),
      );
      dispatch(
        setCurrentFileUploadProperties({
          workspaceId: selectedFile[0]!.spaceId,
          hierarchyId: selectedFile[0]!.hierarchyId,
          currentFolderRef: currentFolder ?? undefined,
          isFolderUpload: false,
          isUploadAllowed: true,
        }),
      );
      dispatch(setIsFileVersionUploading(true));
      dispatch(maximiseFileUpload());
    }
  };

  const handleSingleAccordionStateUpdate = (type: string) => {
    setAccordianState({
      ...accordianState,
      [type]: !accordianState[type],
    });
  };

  const accordionContentMapper: Record<string, () => React.ReactNode> = {
    details: detailsContent,
    customFields: customFieldsContent,
    CustomMetadata: customMetadataContent,
    access: accessContent,
    nomenclature: nomenclatureContent,
    constituentFiles: constituentContent,
    externalLinks: externalLinksContent,
    transmittals: sharingContent,
    verisons: versionContent,
    reviews: fileStatusInReviewsContent,
    dependencies: dependencyContent,
    revisions: revisionContent,
  };

  const accordionHeader = (data: {
    id: string;
    name?: string;
    headerName?: string;
    headerIcon?: string;
    dataTestIdHeaderIcon?: string;
    dataTestIdHeaderName?: string;
  }) => (
    <div className="flex ps-5 items-center gap-4">
      <IconComponent dataTestId={data.dataTestIdHeaderIcon} name={data.headerIcon} width="1.25rem" height="1.25rem" />
      <div className="font-semibold">
        <Text data-testid={data.dataTestIdHeaderName}>{data.headerName}</Text>
      </div>
    </div>
  );

  const accordionContent = (data: { id: string }) => (
    <div className="ps-3">
      {/* One throwing section must degrade to ITS OWN error card, never take the whole panel down —
          "the details panel is not opening" is strictly worse than one broken section, and this
          panel has a dozen sections owned by different features (QA r3 #5 hardening). */}
      <PanelSectionBoundary
        sectionId={data.id}
        fallback={
          <Text className="block py-3 text-xs text-text_light">
            {t("Something went wrong while loading this section.")}
          </Text>
        }
      >
        {accordionContentMapper[data.id]!()}
      </PanelSectionBoundary>
    </div>
  );

  const versionAccordion = () => {
    const resourceType = selectedFile[0]?.resourceType;

    const sidebarMapper = [
      {
        id: "details",
        headerName: t("Details"),
        headerIcon: "details",
        allow: true,
        accordionState: "openDetails",
        dataTestIdHeader: "detailsHeader",
        dataTestIdHeaderName: "detailsHeaderName",
        dataTestIdContent: "detailsHeaderContent",
        dataTestIdHeaderIcon: "detailsHeaderIcon",
      },
      {
        id: "customFields",
        headerName: (["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType)
          ? t("Templates")
          : t("Custom Fields"),
        headerIcon: "CustomFields",
        allow:
          selectedFormTemplateData.formTemplate?.length ||
          (["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openCustomFiles",
      },
      {
        id: "CustomMetadata",
        headerName: t("Templates"),
        headerIcon: "CustomMetadata",
        allow: [BIM_ENGINE, CAD_ENGINE].includes(_.get(selectedFile, "[0].engineType")!),
        accordionState: "openCustomMetadata",
      },
      {
        id: "access",
        headerName: t("Access"),
        dataTestIdHeader: "accesssHeader",
        dataTestIdContent: "accesssHeaderContent",
        headerIcon: "access",
        allow: (
          [
            "RESOURCE_TYPE_FOLDER",
            "RESOURCE_TYPE_MERGED_FILE",
            "RESOURCE_TYPE_FILE",
            "RESOURCE_TYPE_TRANSMITTAL",
            "RESOURCE_TYPE_SHORTCUT_FILE",
            "RESOURCE_TYPE_EMBED",
          ] as (string | undefined)[]
        ).includes(resourceType),
        accordionState: "openAccess",
      },
      {
        id: "externalLinks",
        headerName: t("External Links"),
        headerIcon: "Link",
        // Files and folders only, matching the server's `resourceType` enum — sharing a transmittal,
        // estimate or schedule is an explicit v1 non-goal, so the section is absent rather than
        // present-and-empty on those rows. Gated on the portal switch too (PRD 2).
        allow:
          isExternalSharingEnabled(layout) &&
          (
            ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FOLDER"] as (string | undefined)[]
          ).includes(resourceType),
        accordionState: "openExternalLinks",
        dataTestIdHeader: "externalLinksHeader",
        dataTestIdHeaderName: "externalLinksHeaderName",
        dataTestIdHeaderIcon: "externalLinksHeaderIcon",
        dataTestIdContent: "externalLinksHeaderContent",
      },
      {
        id: "nomenclature",
        headerName: t("Nomenclature"),
        headerIcon: "nomenclatureIcon",
        allow: (["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openNomenclature",
      },
      {
        id: "constituentFiles",
        headerName: t("Constituent Files"),
        headerIcon: "ConstituentFilesIcon",
        allow: (["RESOURCE_TYPE_MERGED_FILE"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openConstituent",
        dataTestIdHeader: "constituentFilesHeader",
        dataTestIdHeaderName: "constituentFilesHeaderName",
        dataTestIdHeaderIcon: "constituentFilesHeaderIcon",
        dataTestIdContent: "constituentFilesContent",
      },
      {
        id: "transmittals",
        headerName: t("Transmittals"),
        headerIcon: "Transmittals",
        allow:
          layout.TRANSMITTAL?.ENABLE &&
          (
            ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"] as (string | undefined)[]
          ).includes(resourceType),
        accordionState: "openSharing",
      },
      {
        id: "verisons",
        headerName: t("Versions"),
        headerIcon: "VersionsIcon",
        allow: (
          ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE", "RESOURCE_TYPE_EMBED"] as (
            | string
            | undefined
          )[]
        ).includes(resourceType),
        accordionState: "openVersion",
      },
      {
        id: "reviews",
        headerName: t("Reviews"),
        headerIcon: "Review",
        allow: (["RESOURCE_TYPE_FILE"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openStatusInReviews",
      },
      {
        id: "dependencies",
        headerName: t("Dependencies"),
        headerIcon: "DependenciesIcon",
        allow:
          layout.FILE_PROPERTIES?.DEPENDENCIES?.ENABLE &&
          (["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openDependency",
      },
      {
        id: "revisions",
        headerName: t("Revisions"),
        headerIcon: "RevisionsIcon",
        allow:
          (
            [
              "RESOURCE_TYPE_FILE",
              "RESOURCE_TYPE_MERGED_FILE",
              "RESOURCE_TYPE_SHORTCUT_FILE",
              "RESOURCE_TYPE_FOLDER",
            ] as (string | undefined)[]
          ).includes(resourceType) && layout.FILE_PROPERTIES?.REVISION?.ENABLE,
        accordionState: "openRevision",
      },
    ];

    return (
      <>
        {sidebarMapper.map(
          (data) =>
            data.allow && (
              <Accordion
                key={data.id}
                expanded={accordianState[data.accordionState]}
                onChange={() => handleSingleAccordionStateUpdate(data.accordionState)}
              >
                <AccordionSummary data-testid={data.dataTestIdHeader} expandIcon={<ExpandMoreIcon />}>
                  {accordionHeader(data)}
                </AccordionSummary>
                <AccordionDetails data-testid={data.dataTestIdContent}>{accordionContent(data)}</AccordionDetails>
              </Accordion>
            ),
        )}
      </>
    );
  };

  const handleAccordionStateUpdate = (type: string) => {
    setAccordianState({
      [type]: true,
    });
  };

  const closeFileVersionReviewStatusModal = () => {
    setOpenAllReviews(false);
  };

  const renderSidebarHeader = () => (
    <div className="flex flex-col gap-4 justify-between p-4 rounded-t-[0.5rem] border-b border-b-[#C6D8EC] bg-[#FAFCFF]">
      <div className="flex items-center gap-2 min-w-0">
        {/* @ts-expect-error -- an indexed `FileVersionRow` is passed unguarded where the callee requires a value; the `| undefined` is `noUncheckedIndexedAccess`, and the surrounding code has already bounds-checked the index. */}
        {getIconbasedOnResourceType(selectedFile[0], "h-6 w-6")}
        <Text className="text-[1.25rem] font-semibold text-[#192048] truncate max-w-[20rem]">
          {selectedFile[0]?.name}
        </Text>
      </div>
      {(selectedFile[0]?.userName || selectedFile[0]?.dateCreated) && (
        <div className="flex items-center justify-between">
          <UserNameTagComponent fullName={selectedFile[0].userName} id={selectedFile[0].userId} size="LG" isCircle />
          {selectedFile[0].dateCreated ? (
            <Text className="text-sm text-gray-500">
              {convertDate(Number(selectedFile[0].dateCreated), "dateForText")}
            </Text>
          ) : null}
        </div>
      )}
    </div>
  );

  // `hierarchyId` is `"0"` for the file-manager root sentinel, which is not a File `_id`; only a
  // real ObjectId can be asked for the review status of all its versions.
  const selectedHierarchyId = selectedFile[0]?.hierarchyId;
  const allVersionsReviewFileId = selectedHierarchyId === "0" ? undefined : selectedHierarchyId;

  return (
    <>
      {Array.isArray(selectedFile) && selectedFile.length && !multipleFilesSelected ? (
        <div className="flex flex-col w-full h-full rounded-t-[0.5rem] border border-[#C6D8EC] overflow-hidden">
          {renderSidebarHeader()}
          <div className="flex w-full flex-1 min-h-0 overflow-auto">
            <div ref={versionManagementRef} className="h-full w-full overflow-auto rounded bg-[var(--surface-white)]">
              {versionAccordion()}
            </div>
            <RightSidebarFileExp handleAccordionStateUpdate={handleAccordionStateUpdate} />
          </div>
        </div>
      ) : (
        !multipleFilesSelected && (
          <div className="flex w-full h-full bg-white items-center justify-center flex-col">
            <img src={`${getImageUrl()}/NoData.svg`} alt="No data" />
            <Text>{t("No file selected")}</Text>
          </div>
        )
      )}
      {multipleFilesSelected && (
        <div className="flex w-full h-full bg-white items-center justify-center flex-col">
          <img src={`${getImageUrl()}/NoData.svg`} alt="No data" />
          <Text>{t("Multiple Files Selected")}</Text>
        </div>
      )}

      {openAllReviews && allVersionsReviewFileId && (
        <AllFileVersionStatusInReviewsModal
          fileId={allVersionsReviewFileId}
          closeModal={closeFileVersionReviewStatusModal}
        />
      )}
    </>
  );
}

export default FileVersion;
